import {
  workflowExportSchema,
  WORKFLOW_SCHEMA_VERSION,
  type WorkflowExport,
} from '@/schemas/workflow-export'

/**
 * Hard ceiling enforced before attempting JSON.parse / schema validation.
 * Matches 03-RESEARCH §Pattern 5 (import pre-check, 2MB cap).
 */
export const MAX_IMPORT_SIZE_BYTES = 2 * 1024 * 1024

/**
 * Robust File-to-text reader. Prefers native Blob.text() when available
 * (modern browsers + recent jsdom); falls back to FileReader, which is
 * guaranteed in jsdom builds that predate the Blob.text() plumbing.
 */
function readFileAsText(file: File): Promise<string> {
  const blobText = (file as unknown as { text?: () => Promise<string> }).text
  if (typeof blobText === 'function') {
    const result = blobText.call(file)
    // Some environments return a Promise<string>; make sure we got one
    // with a real string payload before returning.
    return Promise.resolve(result).then((v) =>
      typeof v === 'string' ? v : ''
    ) as Promise<string>
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = () => reject(reader.error ?? new Error('FileReader error'))
    reader.readAsText(file)
  })
}

export type ValidateImportResult =
  | { ok: true; data: WorkflowExport }
  | {
      ok: false
      code:
        | 'file-too-large'
        | 'invalid-json'
        | 'invalid-schema'
        | 'unsupported-version'
      details?: unknown
    }

/**
 * Client-side pre-check before sending the payload to POST /workflows/import.
 * Returns a discriminated union so callers can render a targeted error message.
 */
export async function validateImportFile(
  file: File
): Promise<ValidateImportResult> {
  if (file.size > MAX_IMPORT_SIZE_BYTES) {
    return { ok: false, code: 'file-too-large' }
  }

  // Prefer Blob.text() in browsers; fall back to FileReader for older jsdom
  // builds where File.prototype.text is not wired to the Blob content.
  const text = await readFileAsText(file)
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    return { ok: false, code: 'invalid-json' }
  }

  // If schema_version is present but wrong, surface a more specific code
  // before running the full schema parse (which would also fail).
  const sv =
    parsed && typeof parsed === 'object'
      ? (parsed as Record<string, unknown>).schema_version
      : undefined
  if (typeof sv === 'string' && sv !== WORKFLOW_SCHEMA_VERSION) {
    return { ok: false, code: 'unsupported-version', details: sv }
  }

  const result = workflowExportSchema.safeParse(parsed)
  if (!result.success) {
    return {
      ok: false,
      code: 'invalid-schema',
      details: result.error.issues,
    }
  }
  return { ok: true, data: result.data }
}

/**
 * Slugify a workflow name for use in export filenames.
 * - Strips accents (NFD + combining marks)
 * - Lowercases
 * - Replaces non-alphanumeric runs with a single hyphen
 * - Trims leading/trailing hyphens
 * - Falls back to 'workflow' for empty / all-symbol names
 */
export function slugifyWorkflowName(name: string): string {
  const slug = name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return slug || 'workflow'
}

/**
 * Trigger a browser download of a WorkflowExport payload.
 * `suffix` typically encodes the version (e.g. "v3" or "draft").
 */
export function downloadWorkflowExport(
  data: WorkflowExport,
  suffix: string
): void {
  const filename = `${slugifyWorkflowName(data.name)}-${suffix}.json`
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json',
  })
  const href = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = href
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(href)
}
